//================================================================
//  Component: Improvement Form
//================================================================

//  Purpose: Form for Reporting an Improvement

//  Properties:
//    - setPageStatus = {Function to set the page status after submission}
//    - portals = {Array of portal names to select from}

//  Example:
//    <Improvement
//      setPageStatus={setPageStatus}
//      portals={['Digital Services', 'Invertory', 'Demand Management']}
//    ></Improvement>    

//================================================================

//Libraries
import React, { useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';

//Contexts
import { GetUser } from '../../../Library/GlobalContexts';

//Components

//Functions
import WriteDocument from '../../../Library/WriteDocument';
import UploadFile from '../../../Library/UploadFile';
import DownloadFile from '../../../Library/DownloadFile';

//Images
import MultipleDocumentInput from '../Components/MultipleDocumentInput';

//CSS

export default function Improvement({
    setPageStatus,
    portals,
}) {

    //------------------------------------------------------
    //  useContexts & React Router
    //------------------------------------------------------

    const getUser = useContext(GetUser);

    //------------------------------------------------------
    //  useState
    //------------------------------------------------------

    // State to manage loading status
    const [loading, setLoading] = React.useState(false);

    // URL for the process diagram file
    const [urlLoading, setUrlLoading] = React.useState(false);

    // URL for the process diagram file
    const [fileUrl, setFileUrl] = React.useState(null);

    // --------------------------------------------------
    //  Form State
    // --------------------------------------------------

    const { register, handleSubmit, setError, control, clearErrors, reset, formState: { errors } } = useForm();

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    //  Submit Handler
    const onSubmit = (data) => {

        setLoading(true);

        // Generate a unique case ID based on the current timestamp
        const caseid = `inc-${Date.now()}`;

        // Get the supporting document file from the form data
        const supportingdocFiles = data?.supportingdoc;

        let uploadPromise = [];

        // Only upload if supporting document file exists
        if (Array.isArray(supportingdocFiles) && supportingdocFiles.length > 0) {

            // Map over the supporting document files to create upload promises
           uploadPromise = supportingdocFiles.map(supportingdocFile => {
                if (supportingdocFile instanceof File) {
                    const extension = supportingdocFile.name.split('.').pop();
                    const fileid = `file-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
                    const path = `support/${caseid}/${fileid}.${extension}`;

                    return UploadFile(path, supportingdocFile).then((fileurl) => ({
                        fileid,
                        filename: supportingdocFile.name,
                        fileurl,
                        size: supportingdocFile.size,
                        type: supportingdocFile.type,
                    }));
                }
                return Promise.resolve(null);
            });
        }

        return Promise.all(uploadPromise)
            .then((supportingdocMetaArray) => {
                const validSupportingdocMeta = supportingdocMetaArray.filter(meta => meta !== null);

                const support = {
                    caseid,
                    casetype: 'Improvement',
                    portal: data.portal || '',
                    description: '',
                    link: '',
                    permissionlevel: '',
                    createddate: new Date(),
                    createdby: {
                        email: getUser?.emailaddress,
                        givenname: getUser?.givenname,
                        surname: getUser?.surname,
                    },
                    screenshot: [],
                    problemstatement: data.problemstatement || '',
                    expectedoutcome: data.expectedoutcome || '',
                    businessimpact: data.businessimpact || '',
                    supportingdocs: validSupportingdocMeta.length > 0 ? validSupportingdocMeta : [],
                };

                return WriteDocument('support', caseid, support, true);
            })
            .then(() => {
                setLoading(false);
                setPageStatus('success');
                reset();
            })
            .catch((error) => {
                setLoading(false);
                console.error("onSubmit: Error during submission or file upload:", error);
                setPageStatus('error');
                reset();
            });

    }

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    useEffect(() => {
        setFileUrl(null);
        setUrlLoading(true);

        // Fetch the file URL for the process diagram
        DownloadFile('/artefacts/process_diagram.pdf')
            .then((url) => {
                setFileUrl(url);
                setUrlLoading(false);
            })
            .catch((err) => {
                setUrlLoading(false);
                console.error(`Error fetching file URL for /artefacts/process_diagram.pdf:`, err.message);
            });

    }, []);

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <form className='flex flex-col gap-2 w-full' onSubmit={handleSubmit(onSubmit)}>

            <div className='bg-white rounded-[10px] p-3 shadow-md border-solid border-1 border-neutral-300 h-full '>
                <div className='md:px-3'>

                    {/* ============================ */}
                    {/*          Questions           */}
                    {/* ============================ */}

                    {/*==================1. Which portal does this relate to? *=================*/}
                    <div className='Form-Row'>
                        <label className='font-medium'> 1. Which portal does this relate to? <span className='text-[#C4314B]'>*</span> </label>
                        <select
                            className={errors.portal ? ('Input-Field-Select-Error  md:w-1/2') : ('Input-Field-Select  md:w-1/2')}
                            {...register('portal', {
                                required: 'This field is required',
                            })}
                        >
                            <option hidden value=''>Select one</option>
                            {portals && portals.map((portal, index) => (
                                <option key={index} value={portal}>
                                    {portal}
                                </option>
                            ))}
                        </select>
                        {errors.portal && <p className='font-medium text-sm text-[#DE0000] my-2 mb-0'>{errors.portal?.message}</p>}
                    </div>

                    {/*==================2. What is the problem you are trying to solve? =================*/}
                    <div className='Form-Row'>
                        <label className='font-medium'> 2. What is the problem you are trying to solve? <span className='text-[#C4314B]'>*</span> </label>
                        <textarea
                            className={errors.problemstatement ? ('Input-Field-TextArea-Error  md:w-1/2') : ('Input-Field-TextArea  md:w-1/2')}
                            {...register('problemstatement', {
                                required: 'This field is required',
                            })}
                            placeholder='Describe the problem'
                            autoComplete='off'
                        ></textarea>

                        {errors.problemstatement && <p className='font-medium text-sm text-[#DE0000] my-2 mb-0'>{errors.problemstatement?.message}</p>}
                    </div>

                    {/*==================3. What is the outcome you are looking for? * =================*/}
                    <div className='Form-Row'>
                        <label className='font-medium'> 3. What is the outcome you are looking for?  <span className='text-[#C4314B]'>*</span> </label>
                        <textarea
                            className={errors.expectedoutcome ? ('Input-Field-TextArea-Error  md:w-1/2') : ('Input-Field-TextArea  md:w-1/2')}
                            {...register('expectedoutcome', {
                                required: 'This field is required',
                            })}
                            placeholder='Describe what success would look like'
                            autoComplete='off'
                        ></textarea>

                        {errors.expectedoutcome && <p className='font-medium text-sm text-[#DE0000] my-2 mb-0'>{errors.expectedoutcome?.message}</p>}
                    </div>

                    {/*==================4. What would be the impact be if this didn’t go ahead? * =================*/}
                    <div className='Form-Row'>
                        <label className='font-medium'> 4. What would be the impact be if this didn’t go ahead? <span className='text-[#C4314B]'>*</span> </label>
                        <textarea
                            className={errors.businessimpact ? ('Input-Field-TextArea-Error  md:w-1/2') : ('Input-Field-TextArea  md:w-1/2')}
                            {...register('businessimpact', {
                                required: 'This field is required',
                            })}
                            placeholder='Describe what would happen and any Business impact if this request was not approved'
                            autoComplete='off'
                        ></textarea>

                        {errors.businessimpact && <p className='font-medium text-sm text-[#DE0000] my-2 mb-0'>{errors.businessimpact?.message}</p>}
                    </div>

                    {/* ========== 5. Attach Document========== */}

                    {/* <div className='flex flex-col gap-2 pt-3 pb-4 '>
                        <div className='flex flex-col gap-1 pb-2'>
                            <label className='font-medium'>
                                5. Attach Document
                            </label>
                            <label className='text-[14px] text-slate-500 italic md:w-1/2'>
                                If this relates to improving our workflows, please highlight the specific area you’d like to change and attach the document. To view our process diagrams, click {urlLoading?<img className='w-[30px] h-[30px] self-center' src={pendingIcon} alt='pending-icon'></img>:
                               <Link to={fileUrl} target='_blank' className='text-blue-500 underline'>here</Link>}.
                            </label>
                        </div>

                        <div className='Attach-File-Onload-Container border border-gray-300 rounded-md md:w-1/2'>
                            <input
                                type='file'
                                className='Input-Field-File md:w-1/2'
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (!file) {
                                        setValue('supportingdoc', null);
                                        clearErrors('supportingdoc');
                                        return;
                                    }

                                    if (file.size > 30 * 1024 * 1024) {
                                        setError('supportingdoc', {
                                            type: 'manual',
                                            message: 'File too large. Please upload a file smaller than 30MB.',
                                        });
                                        setValue('supportingdoc', null);
                                        return;
                                    }

                                    clearErrors('supportingdoc');
                                    setValue('supportingdoc', file);
                                }}
                            ></input>
                        </div>

                        {errors.supportingdoc && (
                            <label className='font-medium text-[#DE0000] mt-1'>
                                {errors.supportingdoc.message}
                            </label>
                        )}
                    </div> */}

                    <MultipleDocumentInput
                        control={control}
                        setError={setError}
                        clearErrors={clearErrors}
                        fileUrl={fileUrl}
                        urlLoading={urlLoading}
                    ></MultipleDocumentInput>

                </div>

            </div>

            {/* ===================================== */}
            {/*  Submit Button                        */}
            {/* ===================================== */}
            <div>
                <button className='Primary-Button mt-4 max-md:w-full' type='submit' value='submit' disabled={loading}>{loading ? 'Submitting...' : 'Submit'}</button>
            </div>
        </form>
    )
}